import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, adminNavItems, teamLeaderNavItems, workerNavItems } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  FileText,
  Send,
  Download,
  Copy,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Image as ImageIcon,
  Building2,
  Ruler,
  Anchor,
  Weight,
  Camera,
  Mail,
  Phone,
  User,
  Calendar,
  Cloud,
  ClipboardList,
  Save,
  History
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEATHER_OPTIONS = [
  "Sunny / Clear",
  "Partly Cloudy",
  "Cloudy",
  "Light Rain",
  "Heavy Rain",
  "Snow",
  "Windy",
  "Foggy",
  "Freezing"
];

const STATUS_OPTIONS = [
  { value: "approved", label: "Approved", color: "bg-green-500", icon: CheckCircle2 },
  { value: "conditional", label: "Conditional Approval", color: "bg-yellow-500", icon: AlertTriangle },
  { value: "rejected", label: "Rejected / Unsafe", color: "bg-red-500", icon: XCircle }
];

const SCAFFOLD_STATUS_OPTIONS = [
  { value: "ok", label: "OK / Safe" },
  { value: "needs_repair", label: "Needs Repair" },
  { value: "unsafe", label: "Unsafe" }
];

export default function ScaffoldControlReport() {
  const { projectId, reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!reportId;
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previousReports, setPreviousReports] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  
  // Report data
  const [reportCode, setReportCode] = useState("");
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split("T")[0]);
  const [inspectorName, setInspectorName] = useState("");
  const [inspectorCode, setInspectorCode] = useState("");
  const [weatherConditions, setWeatherConditions] = useState("");
  const [generalNotes, setGeneralNotes] = useState("");
  const [overallStatus, setOverallStatus] = useState("approved");
  
  // Scaffolds
  const [scaffolds, setScaffolds] = useState([createEmptyScaffold()]);
  
  // Recipient
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");

  function createEmptyScaffold() {
    return {
      scaffold_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: "",
      height: "",
      length: "",
      width: "",
      capacity: "",
      number_of_anchors: "",
      photos: [],
      signed_card_photo: "",
      notes: "",
      status: "ok"
    };
  }

  useEffect(() => {
    fetchData();
  }, [projectId, reportId]);

  const fetchData = async () => {
    try {
      // Get project info
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
      
      // Get previous reports for import
      const reportsRes = await axios.get(`${API}/projects/${projectId}/control-reports`);
      setPreviousReports(reportsRes.data.reports || []);
      
      // If editing, get report data
      if (reportId) {
        const reportRes = await axios.get(`${API}/projects/${projectId}/control-reports/${reportId}`);
        const report = reportRes.data;
        
        setReportCode(report.report_code || "");
        setInspectionDate(report.inspection_date || new Date().toISOString().split("T")[0]);
        setInspectorName(report.inspector_name || "");
        setInspectorCode(report.inspector_code || "");
        setWeatherConditions(report.weather_conditions || "");
        setGeneralNotes(report.general_notes || "");
        setOverallStatus(report.overall_status || "approved");
        setScaffolds(report.scaffolds?.length > 0 ? report.scaffolds : [createEmptyScaffold()]);
        
        if (report.recipient) {
          setRecipientName(report.recipient.name || "");
          setRecipientEmail(report.recipient.email || "");
          setRecipientPhone(report.recipient.phone || "");
        }
      } else {
        // Set defaults for new report
        setInspectorName(user?.name || "");
        
        // Get inspector code from worker profile
        try {
          const profileRes = await axios.get(`${API}/worker/profile`);
          setInspectorCode(profileRes.data?.worker_code || "");
        } catch (err) {
          // No profile, that's ok
        }
        
        // If project has client info, pre-fill recipient
        if (projectRes.data.contact_email) {
          setRecipientEmail(projectRes.data.contact_email);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleImportReport = (report) => {
    // Import scaffolds from previous report
    const importedScaffolds = report.scaffolds.map(s => ({
      ...s,
      scaffold_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      // Clear photos for new inspection
      photos: [],
      signed_card_photo: ""
    }));
    
    setScaffolds(importedScaffolds.length > 0 ? importedScaffolds : [createEmptyScaffold()]);
    setWeatherConditions(report.weather_conditions || "");
    setGeneralNotes("");
    setOverallStatus("approved");
    
    if (report.recipient) {
      setRecipientName(report.recipient.name || "");
      setRecipientEmail(report.recipient.email || "");
      setRecipientPhone(report.recipient.phone || "");
    }
    
    setImportDialogOpen(false);
    toast.success(`Imported ${importedScaffolds.length} scaffolds from previous report`);
  };

  const addScaffold = () => {
    setScaffolds([...scaffolds, createEmptyScaffold()]);
  };

  const removeScaffold = (index) => {
    if (scaffolds.length === 1) {
      toast.error("You must have at least one scaffold");
      return;
    }
    setScaffolds(scaffolds.filter((_, i) => i !== index));
  };

  const updateScaffold = (index, field, value) => {
    const updated = [...scaffolds];
    updated[index] = { ...updated[index], [field]: value };
    setScaffolds(updated);
  };

  const handlePhotoUpload = async (index, files, isSignedCard = false) => {
    if (!files || files.length === 0) return;
    
    try {
      const uploadedUrls = [];
      
      for (let file of files) {
        // Get Cloudinary signature
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=projects&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        // Upload to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", api_key);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        formData.append("folder", folder);
        
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );
        
        const cloudinaryData = await cloudinaryRes.json();
        
        if (cloudinaryData.secure_url) {
          uploadedUrls.push(cloudinaryData.secure_url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        if (isSignedCard) {
          updateScaffold(index, "signed_card_photo", uploadedUrls[0]);
        } else {
          const currentPhotos = scaffolds[index].photos || [];
          updateScaffold(index, "photos", [...currentPhotos, ...uploadedUrls]);
        }
        toast.success("Photo uploaded");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    }
  };

  const removePhoto = (scaffoldIndex, photoIndex) => {
    const updated = [...scaffolds];
    updated[scaffoldIndex].photos = updated[scaffoldIndex].photos.filter((_, i) => i !== photoIndex);
    setScaffolds(updated);
  };

  const handleSave = async (asDraft = false) => {
    setSaving(true);
    
    try {
      const payload = {
        scaffolds: scaffolds.map(s => ({
          ...s,
          height: s.height ? parseFloat(s.height) : null,
          length: s.length ? parseFloat(s.length) : null,
          width: s.width ? parseFloat(s.width) : null,
          capacity: s.capacity ? parseFloat(s.capacity) : null,
          number_of_anchors: s.number_of_anchors ? parseInt(s.number_of_anchors) : null
        })),
        inspection_date: inspectionDate,
        inspector_name: inspectorName,
        inspector_code: inspectorCode,
        weather_conditions: weatherConditions,
        general_notes: generalNotes,
        overall_status: overallStatus,
        recipient: recipientName || recipientEmail ? {
          name: recipientName,
          email: recipientEmail,
          phone: recipientPhone
        } : null
      };
      
      if (isEdit) {
        await axios.put(`${API}/projects/${projectId}/control-reports/${reportId}`, payload);
        toast.success("Report updated");
      } else {
        const res = await axios.post(`${API}/projects/${projectId}/control-reports`, payload);
        setReportCode(res.data.report_code);
        toast.success(`Report created: ${res.data.report_code}`);
        // Navigate to edit mode
        navigate(`/projects/${projectId}/control-reports/${res.data.report_id}`, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      toast.error("Please enter recipient email");
      return;
    }
    
    // Save first if needed
    if (!reportCode) {
      await handleSave();
    }
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/control-reports/${reportId}/send`, {
        name: recipientName,
        email: recipientEmail,
        phone: recipientPhone
      });
      
      toast.success(`Report sent to ${recipientEmail}`);
      setSendDialogOpen(false);
      fetchData(); // Refresh to show sent status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportId) {
      toast.error("Please save the report first");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/projects/${projectId}/control-reports/${reportId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AllStillas_Control_Report_${project?.title?.replace(/\s+/g, '_').slice(0, 30)}_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  // Determine nav items based on user role
  const getNavItems = () => {
    switch (user?.role) {
      case "platform_admin":
      case "operations_manager":
        return adminNavItems;
      case "team_leader":
        return teamLeaderNavItems;
      case "worker":
        return workerNavItems;
      default:
        return adminNavItems;
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={getNavItems()} title="Scaffold Control Report">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={getNavItems()} title="Scaffold Control Report">
      <div className="space-y-6" data-testid="scaffold-control-report">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <Link
              to={`/projects/${projectId}`}
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Project
            </Link>
            <h1 className="text-2xl md:text-3xl font-['Unbounded'] font-bold text-white">
              Scaffold Control Report
            </h1>
            <p className="text-[#94A3B8]">
              Project: {project?.title}
            </p>
            {reportCode && (
              <div className="flex items-center gap-2 mt-2">
                <span className="px-3 py-1 bg-[#23F7DD]/20 text-[#23F7DD] rounded-full text-sm font-mono">
                  {reportCode}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(reportCode);
                    toast.success("Report code copied");
                  }}
                  className="text-[#94A3B8] hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            {previousReports.length > 0 && !isEdit && (
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-white/20 text-white">
                    <History className="w-4 h-4 mr-2" />
                    Import Previous
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Unbounded']">Import Previous Report</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {previousReports.map((report) => (
                      <div
                        key={report.report_id}
                        className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#23F7DD]/50 cursor-pointer transition-colors"
                        onClick={() => handleImportReport(report)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-[#23F7DD]">{report.report_code}</span>
                          <span className="text-xs text-[#94A3B8]">{report.inspection_date}</span>
                        </div>
                        <p className="text-sm text-[#94A3B8]">
                          {report.scaffolds?.length || 0} scaffolds • {report.inspector_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Download PDF Button - only show for saved reports */}
            {isEdit && reportCode && (
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
            
            <Button
              onClick={() => handleSave()}
              disabled={saving}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
            >
              {saving ? (
                <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin mr-2"></span>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEdit ? "Update Report" : "Save Report"}
            </Button>
          </div>
        </div>

        {/* Report Details */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#23F7DD]" />
              Inspection Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-[#94A3B8]">Inspection Date *</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  type="date"
                  value={inspectionDate}
                  onChange={(e) => setInspectionDate(e.target.value)}
                  className="bg-black/50 border-white/20 text-white pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#94A3B8]">Inspector Name *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={inspectorName}
                  onChange={(e) => setInspectorName(e.target.value)}
                  placeholder="Your name"
                  className="bg-black/50 border-white/20 text-white pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#94A3B8]">Inspector Code</Label>
              <Input
                value={inspectorCode}
                onChange={(e) => setInspectorCode(e.target.value)}
                placeholder="TL-001, WRK-003..."
                className="bg-black/50 border-white/20 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-[#94A3B8]">Weather Conditions</Label>
              <div className="relative mt-1">
                <Cloud className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Select value={weatherConditions} onValueChange={setWeatherConditions}>
                  <SelectTrigger className="bg-black/50 border-white/20 text-white pl-10">
                    <SelectValue placeholder="Select weather" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {WEATHER_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scaffolds Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-['Unbounded'] font-bold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#23F7DD]" />
              Scaffolds ({scaffolds.length})
            </h2>
            <Button
              onClick={addScaffold}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Scaffold
            </Button>
          </div>

          {scaffolds.map((scaffold, index) => (
            <Card key={scaffold.scaffold_id} className="bg-[#0A0A0A] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white font-['Unbounded'] text-lg">
                  Scaffold #{index + 1}
                </CardTitle>
                {scaffolds.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeScaffold(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Scaffold Name */}
                <div>
                  <Label className="text-[#94A3B8]">Scaffold Name/ID</Label>
                  <Input
                    value={scaffold.name}
                    onChange={(e) => updateScaffold(index, "name", e.target.value)}
                    placeholder="e.g., Main Building - East Side"
                    className="bg-black/50 border-white/20 text-white mt-1"
                  />
                </div>

                {/* Dimensions Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] flex items-center gap-1">
                      <Ruler className="w-3 h-3" /> Height (m)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={scaffold.height}
                      onChange={(e) => updateScaffold(index, "height", e.target.value)}
                      placeholder="0.0"
                      className="bg-black/50 border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] flex items-center gap-1">
                      <Ruler className="w-3 h-3" /> Length (m)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={scaffold.length}
                      onChange={(e) => updateScaffold(index, "length", e.target.value)}
                      placeholder="0.0"
                      className="bg-black/50 border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] flex items-center gap-1">
                      <Ruler className="w-3 h-3" /> Width (m)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={scaffold.width}
                      onChange={(e) => updateScaffold(index, "width", e.target.value)}
                      placeholder="0.0"
                      className="bg-black/50 border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] flex items-center gap-1">
                      <Weight className="w-3 h-3" /> Capacity (kg/m²)
                    </Label>
                    <Input
                      type="number"
                      value={scaffold.capacity}
                      onChange={(e) => updateScaffold(index, "capacity", e.target.value)}
                      placeholder="200"
                      className="bg-black/50 border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] flex items-center gap-1">
                      <Anchor className="w-3 h-3" /> Nr. Anchors
                    </Label>
                    <Input
                      type="number"
                      value={scaffold.number_of_anchors}
                      onChange={(e) => updateScaffold(index, "number_of_anchors", e.target.value)}
                      placeholder="0"
                      className="bg-black/50 border-white/20 text-white mt-1"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <Label className="text-[#94A3B8]">Scaffold Status</Label>
                  <Select 
                    value={scaffold.status} 
                    onValueChange={(v) => updateScaffold(index, "status", v)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/20 text-white mt-1 w-full md:w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {SCAFFOLD_STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-[#94A3B8]">Notes / Observations</Label>
                  <Textarea
                    value={scaffold.notes}
                    onChange={(e) => updateScaffold(index, "notes", e.target.value)}
                    placeholder="Any observations, issues, or notes about this scaffold..."
                    className="bg-black/50 border-white/20 text-white mt-1 min-h-[80px]"
                  />
                </div>

                {/* Photos Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Scaffold Photos */}
                  <div className="p-4 bg-white/5 rounded-lg border border-dashed border-white/20">
                    <Label className="text-[#94A3B8] flex items-center gap-2 mb-3">
                      <ImageIcon className="w-4 h-4" />
                      Scaffold Photos
                    </Label>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {scaffold.photos?.map((photo, photoIdx) => (
                        <div key={photoIdx} className="relative group">
                          <img
                            src={photo}
                            alt={`Scaffold ${index + 1} photo ${photoIdx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-white/20"
                          />
                          <button
                            onClick={() => removePhoto(index, photoIdx)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <label className="flex items-center justify-center gap-2 px-4 py-3 bg-black/30 rounded-lg cursor-pointer hover:bg-black/50 transition-colors border border-white/10">
                      <Upload className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-sm text-[#94A3B8]">Upload Photos</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(index, e.target.files)}
                      />
                    </label>
                  </div>

                  {/* Signed Card Photo */}
                  <div className="p-4 bg-white/5 rounded-lg border border-dashed border-white/20">
                    <Label className="text-[#94A3B8] flex items-center gap-2 mb-3">
                      <Camera className="w-4 h-4" />
                      Signed Control Card Photo
                    </Label>
                    
                    {scaffold.signed_card_photo ? (
                      <div className="relative group mb-3">
                        <img
                          src={scaffold.signed_card_photo}
                          alt="Signed control card"
                          className="w-full h-32 object-cover rounded-lg border border-white/20"
                        />
                        <button
                          onClick={() => updateScaffold(index, "signed_card_photo", "")}
                          className="absolute top-2 right-2 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center gap-2 px-4 py-6 bg-black/30 rounded-lg cursor-pointer hover:bg-black/50 transition-colors border border-white/10">
                        <Camera className="w-8 h-8 text-[#23F7DD]" />
                        <span className="text-sm text-[#94A3B8]">Upload Signed Card</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(index, e.target.files, true)}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add Scaffold Button at Bottom */}
          <Button
            onClick={addScaffold}
            variant="outline"
            className="w-full border-dashed border-white/30 text-[#94A3B8] hover:text-white hover:border-[#23F7DD] py-8"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Another Scaffold
          </Button>
        </div>

        {/* Overall Status & Notes */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">Overall Assessment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#94A3B8] mb-3 block">Overall Status *</Label>
              <div className="flex flex-wrap gap-3">
                {STATUS_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setOverallStatus(opt.value)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                        overallStatus === opt.value
                          ? `${opt.color} border-transparent text-white`
                          : "bg-white/5 border-white/20 text-[#94A3B8] hover:border-white/40"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div>
              <Label className="text-[#94A3B8]">General Notes</Label>
              <Textarea
                value={generalNotes}
                onChange={(e) => setGeneralNotes(e.target.value)}
                placeholder="Any general observations, recommendations, or notes about the inspection..."
                className="bg-black/50 border-white/20 text-white mt-1 min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Client / Recipient Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#23F7DD]" />
              Send Report to Client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Client Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Client name"
                    className="bg-black/50 border-white/20 text-white pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#94A3B8]">Client Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="bg-black/50 border-white/20 text-white pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#94A3B8]">Client Phone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="+47 XXX XX XXX"
                    className="bg-black/50 border-white/20 text-white pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 pt-4">
              <Button
                onClick={() => handleSave()}
                disabled={saving}
                variant="outline"
                className="border-white/20 text-white flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? "Save Changes" : "Save as Draft"}
              </Button>
              
              <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    disabled={!recipientEmail}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold flex-1"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Report to Client
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle className="font-['Unbounded']">Send Control Report</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-[#94A3B8]">
                      The report will be sent to <strong className="text-white">{recipientEmail}</strong>
                    </p>
                    
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-sm text-[#94A3B8] mb-2">File name:</p>
                      <p className="font-mono text-[#23F7DD]">
                        AllStillas_Control_Report_{project?.title?.replace(/\s+/g, '_').slice(0, 30)}_{new Date().toISOString().split('T')[0].replace(/-/g, '')}.pdf
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setSendDialogOpen(false)}
                        className="flex-1 border-white/20 text-white"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSend}
                        disabled={sending}
                        className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                      >
                        {sending ? (
                          <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin mr-2"></span>
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Now
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
